/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
// UI Frameworks
import { Table, Typography, Icon, Button, Divider } from "antd";
// Local Components
import CreateAccount from "./components/CreateAccount";
// Hooks and utilities
import { isEmpty } from "lodash";
import jMoment from "moment-jalaali";
import { shebaFormat } from "helpers/index";
import BankAccountService, { AccountModel, AccountStatus, AccountType } from "services/http/endpoints/bank-accounts";
// styles
import styles from "./index.module.scss";

const { Text, Title } = Typography;

const BankAccounts: React.FC = () => {
	const [loading, setLoading] = React.useState(true);
	const [showModal, setShowModal] = React.useState(false);
	const [pagination, setPagination] = React.useState();
	const [accounts, setAccounts] = React.useState([] as AccountModel[]);

	function onFinishModal() {
		setShowModal(false);
		fetchData({ size: 10, page: 1 });
	}

	function onCloseModal() {
		setShowModal(false);
	}

	function onOpenModal() {
		setShowModal(true);
	}

	const deleteAccount = async (id: string) => {
		if (window.confirm("آیا برای حذف این حساب مطمئن هستید؟")) {
			try {
				setLoading(true);
				await BankAccountService.delete(id);
				// Fetch accounts after delete
				fetchData({ size: 10, page: 1 });
			} catch (error) {
				setLoading(false);
			}
		}
	};

	const fetchData = async (params?: any) => {
		try {
			setLoading(true);
			BankAccountService.clearOrder();
			BankAccountService.clearFilter();
			BankAccountService.size = params.size;
			BankAccountService.page = params.page;
			BankAccountService.makeOrderBy("createdDate", false);
			// BankAccountService.makeFilters("accountHeadline", "EQ", "USER");
			if (typeof params.filters === "object") {
				// eslint-disable-next-line array-callback-return
				Object.keys(params.filters).map(key => {
					if (params.filters[key].length > 0) {
						BankAccountService.makeFilters(key, "IN", params.filters[key].join());
					}
				});
			}
			const result = await BankAccountService.search(BankAccountService.getSearchFilter());
			const pager = { ...pagination };
			// Read total count from server
			pager.total = result.pagination.total;

			setLoading(false);
			setPagination(pager);
			setAccounts(result.result);
		} catch (error) {
			setLoading(false);
		}
	};

	// get facilities
	React.useEffect(() => {
		fetchData({ size: 10, page: 1 });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function handleTableChange(_pagination: any, filters: any, sorter: any) {
		const pager = { ...pagination };
		pager.current = _pagination.current;
		setPagination({
			pagination: pager,
		});
		fetchData({
			size: _pagination.pageSize,
			page: _pagination.current,
			sortField: sorter.field,
			sortOrder: sorter.order,
			filters,
		});
	}

	function accountStatusFormatted(status: string) {
		switch (status) {
			case AccountStatus.PENDING:
				return (
					<Text>
						<Icon type="clock-circle" /> درحال بررسی
					</Text>
				);
			case AccountStatus.REJECTED:
				return (
					<Text type="danger">
						<Icon type="close-circle" /> رد شده
					</Text>
				);
			case AccountStatus.CONFIRMED:
				return (
					<Text style={{ color: "#0b8235" }}>
						<Icon type="check-circle" /> تأیید شده
					</Text>
				);
			default:
				return "-";
		}
	}

	function accountTypeFormatted(status: string) {
		switch (status) {
			case AccountType.CARD:
				return "شماره کارت";
			case AccountType.SHABA:
				return "شماره شبا";
			default:
				return "-";
		}
	}

	const columns = [
		{
			title: "نام",
			dataIndex: "name",
			key: "name",
			render: (data: any) => {
				return <span>{data}</span>;
			},
		},
		{
			title: "نوع",
			dataIndex: "type",
			key: "type",
			filters: [
				{ text: "شماره شبا", value: "SHABA" },
				{ text: "شماره کارت", value: "CARD" },
			],
			render: (data: any) => {
				return accountTypeFormatted(data);
			},
		},
		{
			title: "وضعیت",
			dataIndex: "status",
			key: "status",
			filters: Object.keys(AccountStatus).map(value => {
				return { text: accountStatusFormatted(value), value };
			}),
			render: (data: any) => {
				return <span>{accountStatusFormatted(data)}</span>;
			},
		},
		{
			title: "شماره",
			dataIndex: "value",
			key: "value",
			render: (data: any, record: any) => {
				return (
					<span className="ltr">
						{data
							? record.type === "SHABA"
								? `IR${shebaFormat(data)}`
								: data.match(/\d{4}/g).join("-")
							: "-"}
					</span>
				);
			},
		},
		{
			title: "تاریخ ثبت",
			dataIndex: "createdDate",
			key: "createdDate",
			render: (data: any) => {
				return <span dir={"ltr"}>{jMoment(data).format("jYYYY/jMM/jDD HH:mm")}</span>;
			},
		},
		{
			title: "",
			dataIndex: "id",
			key: "id",
			render: (data: string) => {
				return (
					<Button size="small" type="danger" shape="round" icon="delete" onClick={() => deleteAccount(data)}>
						حذف
					</Button>
				);
			},
		},
	];

	return (
		<div className="container">
			<div className={styles["financial"]}>
				<Title level={4}>
					<Icon type="bank" /> حسابهای بانکی
					<Divider type="vertical" />
					<Button type="primary" shape="round" size="small" icon="plus" onClick={onOpenModal}>
						افزودن حساب
					</Button>
				</Title>
				<Table
					bordered
					loading={loading}
					pagination={pagination}
					className={styles["table"]}
					onChange={handleTableChange}
					scroll={{ x: true, y: false }}
					dataSource={
						!isEmpty(accounts)
							? accounts.map((data: AccountModel, key: number) => ({
									key,
									id: data.id,
									bank: data.bank,
									name: data.name,
									status: data.status,
									type: data.type,
									value: data.value,
									description: data.description,
									createdDate: data.createdDate,
							  }))
							: []
					}
					columns={columns}
				/>
			</div>
			<CreateAccount shouldVisible={showModal} onClose={onCloseModal} onFinish={onFinishModal} />
		</div>
	);
};

export default BankAccounts;
